import { useCallback, useEffect, useRef, useState } from 'react';
import { useMountEffect } from './use-mount-effect';

export interface UseDebouncedValueOptions {
  leading?: boolean;
}

export type UseDebouncedValueReturnValue<T> = [T, () => void];

export function useDebouncedValue<T = unknown>(
  value: T,
  wait: number,
  options: UseDebouncedValueOptions = { leading: false },
): UseDebouncedValueReturnValue<T> {
  const [_value, setValue] = useState(value);
  const mountedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const cooldownRef = useRef(false);

  const cancel = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (mountedRef.current) {
      if (cooldownRef.current === false && options.leading === true) {
        cooldownRef.current = true;
        // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
        setValue(value);
      } else {
        cancel();
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          setValue(value);
        }, wait);
      }
    }
  }, [value, options.leading, wait, cancel]);

  useMountEffect(() => {
    mountedRef.current = true;
    return cancel;
  });

  return [_value, cancel];
}
